import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BomRoutingStepClient,
  BomRoutingStepDependencyClient,
  ProcessClient,
  RoutingClient,
  StationClient,
  formatNumber,
  refusalMessage,
  type BomDTO,
  type BomRoutingStepDependencyDTO,
  type BomRoutingStepDTO,
  type RoutingDependencyRule,
  type RoutingProposalDTO,
} from "../../api/manufacturing";

/** A step as the editor shows it: with what it waits for, and how. */
type RoutingRow = BomRoutingStepDTO & { waits: number[]; wait_rule: RoutingDependencyRule };

/**
 * A revision's routing: the processes the part goes through, in order, the
 * machines allowed at each (any one of them; none means any machine of the
 * process) and the program the machine runs.
 *
 * <p>Each step says what it waits for: as parts come (FLOW: no more than the
 * other has passed) or once the other is done (FINISH). A new step waits, by
 * default, for every step of the stage before it (Cut, then Edge and
 * Machining side by side, Paint, Inspection); "Reset to defaults" brings the
 * whole routing back to that. Steps are listed by stage, so steps side by
 * side are next to each other.</p>
 *
 * <p>A routing is a draft until approved; a lot is released only on approved
 * routings, and an approved routing is frozen until it is put back to draft
 * (running lots keep what they were released with).</p>
 *
 * <p>When the revision has no routing, the one the part's own fields describe
 * (cut if it has a size, edge if it has edgebands, the machines its machine
 * list names, paint, inspection) is shown as a proposal, with one click to
 * keep it as a draft to review.</p>
 */
export function RoutingStepsPanel({
  partId,
  bom,
  readOnly,
}: {
  partId: number;
  bom: BomDTO;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const steps = useMemo(() => new BomRoutingStepClient(http), [http]);
  const dependencies = useMemo(() => new BomRoutingStepDependencyClient(http), [http]);
  const routings = useMemo(() => new RoutingClient(http), [http]);
  const approved = bom.routing_status === "APPROVED";
  // An archived revision is history: what was built, not what will be. An approved routing is frozen.
  const editable = !readOnly && bom.status !== "ARCHIVED" && !approved;

  const { data: stepRows, isLoading } = useQuery({
    queryKey: ["manufacturing", "bom-routing-step", bom.id],
    queryFn: async () =>
      (await steps.find({ page: 0, size: 200, filters: { bom_id: [String(bom.id)] }, sort: ["position,asc"] })).content,
  });
  const waitsFor = useQuery({
    queryKey: ["manufacturing", "bom-routing-step-dependency", bom.id],
    queryFn: () => dependencies.forBom(bom.id),
  });
  // By stage, then position: steps side by side sit together.
  const rows: RoutingRow[] | undefined = useMemo(() => {
    if (!stepRows) return undefined;
    const deps = waitsFor.data ?? [];
    return [...stepRows]
      .sort((a, b) => (a.process_stage ?? 50) - (b.process_stage ?? 50) || a.position - b.position)
      .map((step) => {
        const mine = deps.filter((dep) => dep.bom_routing_step_id === step.id);
        return {
          ...step,
          waits: mine.map((dep) => dep.depends_on_bom_routing_step_id),
          wait_rule: mine.some((dep) => dep.rule === "FINISH") ? "FINISH" : "FLOW",
        };
      });
  }, [stepRows, waitsFor.data]);
  const { processes, stations } = usePlant();
  const empty = rows !== undefined && rows.length === 0;

  const proposal = useQuery({
    queryKey: ["manufacturing", "routing", "derived", partId],
    queryFn: () => routings.derived(partId),
    enabled: empty,
    staleTime: 60_000,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-routing-step"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-routing-step-dependency"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "routing", "review"] });
  };

  /**
   * A step's dependencies to what the editor holds: the ones dropped are
   * deleted, the new ones written, the rule changed where it differs. A new
   * step left with none keeps the stage defaults the server gave it.
   */
  const syncWaits = async (stepId: number, waits: number[], rule: RoutingDependencyRule, isNew: boolean) => {
    if (isNew && waits.length === 0) return;
    const current = (await dependencies.find({ page: 0, size: 200, filters: { bom_routing_step_id: [String(stepId)] } })).content;
    for (const dep of current) {
      if (!waits.includes(dep.depends_on_bom_routing_step_id)) await dependencies.remove(dep.id);
      else if (dep.rule !== rule) await dependencies.update({ ...dep, rule });
    }
    for (const on of waits) {
      if (!current.some((dep) => dep.depends_on_bom_routing_step_id === on)) {
        await dependencies.create({ id: 0, bom_routing_step_id: stepId, depends_on_bom_routing_step_id: on, rule } as BomRoutingStepDependencyDTO);
      }
    }
  };

  // The ACTIVE revision takes the proposal server-side in one transaction; a
  // draft gets the same steps through the plain resource.
  const adopt = useMutation({
    mutationFn: async (proposed: RoutingProposalDTO) => {
      if (bom.status === "ACTIVE") {
        await routings.create(partId);
        return;
      }
      for (const step of proposed.steps) {
        await steps.create({ ...step, id: 0, bom_id: bom.id, is_derived: true });
      }
    },
    onSuccess: async () => {
      await refresh();
      toast.success(t("routing.adopted", "Routing saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const processOptions = processes
    .filter((process) => process.is_routable && process.is_active)
    .map((process) => ({ value: String(process.id), label: `${process.code} · ${process.name_fr}` }));
  const anyMachine = (process_id: number | null | undefined) => {
    const codes = stations
      .filter((station) => station.is_active && station.process_id === process_id)
      .map((station) => station.code);
    return codes.length === 0
      ? t("routing.any-machine", "Any")
      : t("routing.any-of", "Any: {{codes}}", { codes: codes.join(", ") });
  };
  const stationOptions = stations
    .filter((station) => station.is_active)
    .map((station) => ({ value: String(station.id), label: `${station.code} · ${station.name}` }));

  const stepName = (id: number) => {
    const step = (rows ?? []).find((row) => row.id === id);
    return step ? `${String(step.position)}. ${step.process_name ?? step.process_code ?? ""}` : String(id);
  };
  const ruleLabel = (rule: RoutingDependencyRule) =>
    rule === "FINISH" ? t("routing.rule.finish", "once done") : t("routing.rule.flow", "as parts come");

  const cols = useMemo(() => {
    const c = defineColumns<RoutingRow>();
    return [
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", priority: 3 }),
      c.custom(
        "process",
        (row) => row.process_code ?? "",
        (row) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.process_code}</span>
            <span className="text-[11px] text-ink-faint">{row.process_name}</span>
            {row.is_derived && (
              <Badge variant="warning" size="sm">
                {t("routing.derived", "derived")}
              </Badge>
            )}
            {row.is_external && (
              <Badge variant="neutral" size="sm">
                {t("routing.external", "outside")}
              </Badge>
            )}
          </span>
        ),
        { header: t("routing.process", "Process"), priority: 1 },
      ),
      c.custom(
        "waits",
        (row) => row.waits.map(stepName).join(", "),
        (row) =>
          row.waits.length === 0 ? (
            <span className="text-ink-faint">{t("routing.waits.nothing", "nothing")}</span>
          ) : (
            <span>
              {row.waits.map(stepName).join(", ")}
              <span className="ml-1 text-[11px] text-ink-faint">({ruleLabel(row.wait_rule)})</span>
            </span>
          ),
        { header: t("routing.waits", "Waits for"), priority: 1 },
      ),
      // No machine picked: any active machine of the process, named, so the step says which.
      c.text("stations", (row) => row.station_codes ?? anyMachine(row.process_id), {
        header: t("routing.machines", "Machines"),
        priority: 2,
      }),
      c.text("program", (row) => row.program_code ?? "", { header: t("routing.program", "Program"), priority: 2 }),
      c.text("setup", (row) => (row.setup_minutes == null ? "" : formatNumber(row.setup_minutes)), {
        header: t("routing.setup-minutes", "Setup (min)"),
        priority: 4,
      }),
      c.text("run", (row) => (row.run_minutes == null ? "" : formatNumber(row.run_minutes)), {
        header: t("routing.run-minutes", "Per part (min)"),
        priority: 4,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 5 }),
    ];
    // stepName and ruleLabel read rows and t; rebuilt with them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, rows]);

  const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value));

  if (empty) {
    return (
      <ProposalView
        proposal={proposal.data}
        loading={proposal.isLoading}
        actionLabel={editable ? t("routing.use", "Use this routing") : null}
        busy={adopt.isPending}
        onAdopt={(proposed) => adopt.mutate(proposed)}
        onBlank={
          editable
            ? () =>
                void steps
                  .create({
                    id: 0,
                    bom_id: bom.id,
                    position: 1,
                    process_id: Number(processOptions[0]?.value ?? 0),
                    program_code: null,
                    setup_minutes: null,
                    run_minutes: null,
                    note: null,
                    is_derived: false,
                    station_ids: [],
                  })
                  .then(refresh, (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))))
            : null
        }
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
    <RoutingStatusBar bom={bom} readOnly={readOnly || bom.status === "ARCHIVED"} onChanged={refresh} />
    <DataTable<RoutingRow>
      data={rows ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={isLoading}
      editor={
        editable
          ? {
              fields: {
                position: {
                  kind: "number",
                  get: (row) => (Number.isFinite(row.position) ? row.position : ""),
                  set: (row, value) => ({ ...row, position: value.trim() === "" ? Number.NaN : Number(value) }),
                  required: true,
                },
                process: {
                  kind: "select",
                  get: (row) => (row.process_id ? String(row.process_id) : ""),
                  set: (row, value) => ({ ...row, process_id: Number(value) }),
                  options: processOptions,
                  required: true,
                },
                stations: {
                  kind: "multiselect",
                  get: (row) => row.station_ids.map(String),
                  set: (row, value) => ({ ...row, station_ids: value.map(Number) }),
                  options: stationOptions,
                },
                program: {
                  kind: "text",
                  get: (row) => row.program_code ?? "",
                  set: (row, value) => ({ ...row, program_code: value.trim() === "" ? null : value }),
                },
                setup: {
                  kind: "number",
                  get: (row) => row.setup_minutes ?? "",
                  set: (row, value) => ({ ...row, setup_minutes: optionalNumber(value) }),
                },
                run: {
                  kind: "number",
                  get: (row) => row.run_minutes ?? "",
                  set: (row, value) => ({ ...row, run_minutes: optionalNumber(value) }),
                },
                note: {
                  kind: "text",
                  get: (row) => row.note ?? "",
                  set: (row, value) => ({ ...row, note: value.trim() === "" ? null : value }),
                },
                waits: {
                  kind: "multiselect",
                  get: (row) => row.waits.map(String),
                  set: (row, value) => ({ ...row, waits: value.map(Number) }),
                  options: (rows ?? []).map((other) => ({ value: String(other.id), label: stepName(other.id) })),
                },
              },
              newRow: (): RoutingRow => ({
                id: 0,
                bom_id: bom.id,
                position: Math.max(0, ...(rows ?? []).map((row) => row.position)) + 1,
                process_id: 0,
                program_code: null,
                setup_minutes: null,
                run_minutes: null,
                note: null,
                is_derived: false,
                station_ids: [],
                waits: [],
                wait_rule: "FLOW",
              }),
              validate: (row) => {
                const errors: Record<string, string> = {};
                if (!(Number.isInteger(row.position) && row.position > 0))
                  errors.position = t("validation.whole-number-one", "A whole number, 1 or more.");
                else if ((rows ?? []).some((other) => other.id !== row.id && other.position === row.position))
                  errors.position = t("routing.position-taken", "Another step has this position.");
                if (!row.process_id) errors.process = t("validation.required", "Required");
                if (row.waits.includes(row.id)) errors.waits = t("routing.waits.self", "A step cannot wait for itself.");
                for (const key of ["setup_minutes", "run_minutes"] as const) {
                  const value = row[key];
                  if (value !== null && !(Number.isFinite(value) && value >= 0))
                    errors[key === "setup_minutes" ? "setup" : "run"] = t("validation.not-negative", "Zero or more.");
                }
                return errors;
              },
              onSave: async (row, isNew) => {
                const { waits, wait_rule, ...step } = row;
                try {
                  const saved = isNew ? await steps.create(step) : (await steps.update(step), step);
                  await syncWaits(saved.id, waits, wait_rule, isNew);
                } catch (failure) {
                  throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                }
                await refresh();
                toast.success(t("notification.saved", "Saved"));
              },
              onDelete: async (row) => {
                await steps.remove(row.id);
                await refresh();
                toast.success(t("notification.deleted", "Deleted"));
              },
              deleteConfirm: () => ({ title: t("bom.step.delete", "Remove this step?") }),
              addLabel: t("button.add-step", "Add step"),
            }
          : undefined
      }
      emptyMessage={t("routing.empty", "No routing on this revision.")}
    />
    </div>
  );
}

/**
 * Where the routing stands, and the actions that move it: approve (checked by
 * the server, which says what is wrong), back to draft, reset the waits to the
 * stages' defaults. A routing must be approved before a lot can be released
 * on it.
 */
function RoutingStatusBar({ bom, readOnly, onChanged }: { bom: BomDTO; readOnly: boolean; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const routings = useMemo(() => new RoutingClient(http), [http]);
  const [problem, setProblem] = useState<string | null>(null);
  const approved = bom.routing_status === "APPROVED";
  const act = useMutation({
    mutationFn: async (action: "approve" | "draft" | "defaults") => {
      if (action === "approve") await routings.approve(bom.id);
      else if (action === "draft") await routings.toDraft(bom.id);
      else await routings.resetDependencies(bom.id);
      return action;
    },
    onSuccess: async (action) => {
      setProblem(null);
      await onChanged();
      toast.success(
        action === "approve"
          ? t("routing.approved.done", "Routing approved")
          : action === "draft"
            ? t("routing.draft.done", "Routing back to draft")
            : t("routing.defaults.done", "Waits reset to the defaults"),
      );
    },
    onError: (failure) => setProblem(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2 lg:px-6">
      {approved ? (
        <Badge variant="success" size="sm">
          {t("routing.status.approved", "Approved")}
        </Badge>
      ) : (
        <Badge variant="warning" size="sm">
          {t("routing.status.draft", "Draft")}
        </Badge>
      )}
      <span className="text-[12px] text-ink-muted">
        {approved
          ? t("routing.status.approved-by", "by {{who}} on {{when}}", {
              who: bom.routing_approved_by_name ?? "?",
              when: bom.routing_approved_at ? new Date(bom.routing_approved_at).toLocaleDateString(i18n.language) : "?",
            })
          : t("routing.status.draft-hint", "A lot is released only on an approved routing.")}
      </span>
      {!readOnly && (
        <div className="ml-auto flex flex-wrap gap-2">
          {approved ? (
            <Button size="sm" variant="ghost" loading={act.isPending && act.variables === "draft"} disabled={act.isPending} onClick={() => act.mutate("draft")}>
              {t("routing.to-draft", "Back to draft")}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="ghost" loading={act.isPending && act.variables === "defaults"} disabled={act.isPending} onClick={() => act.mutate("defaults")}>
                {t("routing.reset-defaults", "Reset waits to defaults")}
              </Button>
              <Button size="sm" variant="primary" loading={act.isPending && act.variables === "approve"} disabled={act.isPending} onClick={() => act.mutate("approve")}>
                {t("routing.approve", "Approve")}
              </Button>
            </>
          )}
        </div>
      )}
      {problem && <p className="w-full text-[12px] text-risk">{problem}</p>}
    </div>
  );
}

/** Processes and machines, cached for the session: they change a few times a year. */
function usePlant() {
  const { http } = useAuth();
  const processes = useQuery({
    queryKey: ["process"],
    queryFn: () => new ProcessClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const stations = useQuery({
    queryKey: ["station"],
    queryFn: () => new StationClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  return { processes: processes.data ?? [], stations: stations.data ?? [] };
}

/**
 * The routing a part's fields describe, read-only, with what keeping it does.
 * Shown on a revision with no routing and on a part with no revision at all.
 */
export function ProposalView({
  proposal,
  loading,
  actionLabel,
  busy,
  onAdopt,
  onBlank,
}: {
  proposal: RoutingProposalDTO | undefined;
  loading: boolean;
  /** Null when the routing cannot be kept from here (read-only, archived). */
  actionLabel: string | null;
  busy: boolean;
  onAdopt: (proposal: RoutingProposalDTO) => void;
  onBlank?: (() => void) | null;
}) {
  const { t } = useTranslation();
  if (loading || !proposal) {
    return <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>;
  }
  return (
    <div className="flex flex-col gap-3 px-4 py-3 lg:px-6">
      <p className="text-[13px] text-ink-muted">
        {t(
          "routing.proposal-draft",
          "No routing yet. This is what the part's edge pattern and machine list describe; keep it as a draft, check it, then approve it so lots can be released:",
        )}
      </p>
      <ol className="flex flex-col gap-1.5">
        {proposal.steps.map((step) => (
          <li key={step.position} className="flex flex-wrap items-baseline gap-2 text-[13px]">
            <span className="w-5 text-right tabular-nums text-ink-faint">{step.position}.</span>
            <span className="font-medium">{step.process_name ?? step.process_code}</span>
            {step.station_codes ? <span className="text-ink-muted">{step.station_codes}</span> : null}
            {step.program_code ? (
              <Badge variant="neutral" size="sm">
                {step.program_code}
              </Badge>
            ) : null}
            {step.note ? <span className="text-[12px] text-ink-faint">{step.note}</span> : null}
          </li>
        ))}
      </ol>
      {proposal.unknown_tokens.length > 0 && (
        <p className="text-[12px] text-warn">
          {t("routing.unknown", "Not recognised in the machine list: {{tokens}}", { tokens: proposal.unknown_tokens.join(", ") })}
        </p>
      )}
      {actionLabel && (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" size="sm" loading={busy} onClick={() => onAdopt(proposal)}>
            {actionLabel}
          </Button>
          {onBlank && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onBlank}>
              {t("routing.start-blank", "Start from an empty routing")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * For a part with no revision at all: the derived routing and one click to
 * keep it, which creates the part's first revision holding only the routing.
 */
export function DerivedRoutingForNewPart({ partId, readOnly, onCreated }: { partId: number; readOnly: boolean; onCreated: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const routings = useMemo(() => new RoutingClient(http), [http]);
  const proposal = useQuery({
    queryKey: ["manufacturing", "routing", "derived", partId],
    queryFn: () => routings.derived(partId),
    staleTime: 60_000,
  });
  const create = useMutation({
    mutationFn: () => routings.create(partId),
    onSuccess: async () => {
      await onCreated();
      toast.success(t("routing.adopted", "Routing saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  // A part with nothing to do on the floor derives to Inspection alone: nothing worth offering.
  const worthOffering = (proposal.data?.steps ?? []).some((step) => step.process_code !== "INSPECTION");
  if (!proposal.isLoading && !worthOffering) return null;
  return (
    <ProposalView
      proposal={proposal.data}
      loading={proposal.isLoading}
      actionLabel={readOnly ? null : t("routing.use-new", "Use this routing (creates revision 1)")}
      busy={create.isPending}
      onAdopt={() => create.mutate()}
    />
  );
}
